import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigation, Home, AlertTriangle, Clock, Shield, Users } from 'lucide-react';
import { clsx } from 'clsx';
// 🛰️ Using unified types to ensure the event structure is recognized
import { CalendarEvent } from '@/types';

/**
 * 🛰️ TravelMetadata Interface
 * Strictly defines the shape of the intelligence engine's transit analysis.
 */
interface TravelMetadata {
  estimatedDuration: number;
  isImpossible: boolean;
  travelBackHomeAdvised?: boolean;
  originType?: 'previous_event' | 'home' | 'office';
}

interface TravelBufferProps {
  // We extend the base event type locally to acknowledge the potential metadata
  event: CalendarEvent & { travelMetadata?: TravelMetadata; source?: 'personal' | 'household' };
  hourHeight: number; // Pixels per hour for vertical scaling
}

/**
 * 🛰️ TravelBuffer Component
 * Refactored for Hybrid Scoping: Renders transit gaps with source-aware branding.
 * Emerald = Personal Partition | Indigo = Household Hub
 */
export const TravelBuffer: React.FC<TravelBufferProps> = ({ event, hourHeight }) => {
  const { t } = useTranslation();
  
  const meta = event.travelMetadata;
  const isPersonal = event.source === 'personal';

  // 🛡️ Guard: Exit if no travel data is present or duration is invalid
  if (!meta || typeof meta.estimatedDuration !== 'number' || meta.estimatedDuration <= 0) {
    return null;
  }

  // Calculate Height: (Minutes / 60) * hourHeight
  const rawHeight = (meta.estimatedDuration / 60) * hourHeight;
  const height = Math.max(rawHeight, 12);
  
  // Calculate Top Offset (The buffer sits immediately ABOVE the event)
  const topOffset = -(height);

  return (
    <div 
      className={clsx(
        "absolute left-0 right-0 rounded-t-[1.5rem] px-4 flex flex-col justify-center transition-all duration-300 overflow-hidden z-0 border-l-4",
        meta.isImpossible 
          ? 'bg-rose-500/20 border-rose-500 shadow-[inset_4px_0_10px_rgba(244,63,94,0.1)]' 
          : isPersonal
            ? 'bg-emerald-500/10 border-emerald-400/50'
            : 'bg-indigo-500/10 border-indigo-400/50'
      )}
      style={{ 
        top: `${topOffset}px`, 
        height: `${height}px`,
        width: 'calc(100% - 8px)',
        marginLeft: '4px'
      }}
    >
      {/* --- TRANSIT TELEMETRY --- */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {meta.isImpossible ? (
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600 animate-pulse" />
          ) : isPersonal ? (
            <Shield className="w-3 h-3 text-emerald-500" />
          ) : (
            <Navigation className="w-3 h-3 text-indigo-500" />
          )}
          
          <span className={clsx(
            "text-[9px] font-black uppercase tracking-widest",
            meta.isImpossible 
              ? 'text-rose-600' 
              : isPersonal ? 'text-emerald-600/70' : 'text-indigo-600/70'
          )}>
            {meta.estimatedDuration}m {t('calendar.transit', 'Transit')}
          </span>
        </div>

        {/* 🏠 Context Advice Indicator */}
        {!meta.isImpossible && (
          <div className="flex items-center gap-1.5 opacity-60">
            {meta.travelBackHomeAdvised ? (
              <span title={String(t('calendar.go_home', 'Head home between events'))}>
                <Home className={clsx("w-3 h-3", isPersonal ? "text-emerald-400" : "text-slate-400")} />
              </span>
            ) : (
              <span title={String(t('calendar.stay_out_hint', 'Suggest staying in the area'))}>
                <Clock className={clsx("w-3 h-3", isPersonal ? "text-emerald-400" : "text-slate-400")} />
              </span>
            )}
          </div>
        )}
      </div>

      {/* --- INTELLIGENCE MESSAGES --- */}
      {meta.isImpossible ? (
        <p className="text-[8px] font-black uppercase text-rose-700 leading-tight mt-1 italic tracking-tighter">
          {t('calendar.impossible_travel', 'Impossible Gap')}
        </p>
      ) : (
        !meta.travelBackHomeAdvised && meta.originType === 'previous_event' && (
          <p className={clsx(
            "text-[7px] font-black uppercase mt-1 tracking-tighter",
            isPersonal ? "text-emerald-400/80" : "text-indigo-400/80"
          )}>
            {t('calendar.stay_out', 'Efficiency: Stay Out')}
          </p>
        )
      )}
    </div>
  );
};